import { z } from 'zod';

/**
 * Merchant field values (`field_values.value`, jsonb). One shape per `field_type`:
 *
 *   text      string, 1–1000 characters (trimmed)
 *   number    finite JSON number, at most 12 integer and 6 decimal digits
 *   currency  integer cents ≥ 0 (never a float: "$25,000.50" is stored as 2500050)
 *   date      "YYYY-MM-DD"
 *   dropdown  one of the field's options
 *   boolean   true / false
 *
 * `null` means "cleared". The database re-checks the same rules (app.check_field_value) so a client
 * that skips this code still cannot store a malformed value.
 */

export const FIELD_TYPES = ['text', 'number', 'currency', 'date', 'dropdown', 'boolean'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: 'Text',
  number: 'Number',
  currency: 'Amount ($)',
  date: 'Date',
  dropdown: 'Dropdown',
  boolean: 'Yes / no',
};

export const SYSTEM_KEYS = [
  'business_name',
  'owner_name',
  'email',
  'state',
  'address',
  'phone',
] as const;
export type SystemKey = (typeof SYSTEM_KEYS)[number];

export type FieldValue = string | number | boolean;

/** The parts of a field definition that decide what a value may be. */
export interface FieldSpec {
  type: FieldType;
  options: readonly string[];
  systemKey: SystemKey | null;
}

export const TEXT_MAX = 1000;
const MAX_CENTS = 10_000_000_000_000; // $100 billion
const EMAIL = z.email();

export type ParseResult = { ok: true; value: FieldValue | null } | { ok: false; error: string };

const fail = (error: string): ParseResult => ({ ok: false, error });

/** Parses "$25,000.50", "25000", "1,200.5" into integer cents without floating-point arithmetic. */
export function parseCurrencyToCents(input: string): number | null {
  const s = input
    .trim()
    .replace(/^\$\s*/, '')
    .replace(/,/g, '');
  const m = /^(\d{1,11})(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const whole = Number(m[1]);
  const frac = Number((m[2] ?? '').padEnd(2, '0'));
  const cents = whole * 100 + frac;
  return cents <= MAX_CENTS ? cents : null;
}

/** "$25,000.50" from 2500050 (integer arithmetic only). */
export function formatCents(cents: number): string {
  const whole = Math.trunc(cents / 100);
  const frac = String(cents % 100).padStart(2, '0');
  return `$${whole.toLocaleString('en-US')}.${frac}`;
}

function isValidDate(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < 1900 || y > 2100 || mo < 1 || mo > 12 || d < 1) return false;
  // Day count of the month, leap years included (UTC to stay clear of local time zones).
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return d <= days;
}

const NUMBER_PATTERN = /^-?\d{1,12}(\.\d{1,6})?$/;

/**
 * Checks a stored (or about-to-be-stored) value. Returns an error message, or null when the value is
 * acceptable. `null` (cleared) is always acceptable.
 */
export function checkFieldValue(spec: FieldSpec, value: unknown): string | null {
  if (value === null) return null;
  switch (spec.type) {
    case 'text': {
      if (typeof value !== 'string') return 'must be text';
      if (value.trim() !== value || value === '') return 'must be trimmed, non-empty text';
      if (value.length > TEXT_MAX) return `must be at most ${TEXT_MAX} characters`;
      if (spec.systemKey === 'email' && !EMAIL.safeParse(value).success) {
        return 'must be a valid email address';
      }
      if (spec.systemKey === 'email' && value !== value.toLowerCase()) return 'must be lowercase';
      return null;
    }
    case 'number':
      return typeof value === 'number' &&
        Number.isFinite(value) &&
        NUMBER_PATTERN.test(String(value))
        ? null
        : 'must be a number (up to 12 digits, 6 decimals)';
    case 'currency':
      return typeof value === 'number' &&
        Number.isSafeInteger(value) &&
        value >= 0 &&
        value <= MAX_CENTS
        ? null
        : 'must be a whole number of cents, 0 or more';
    case 'date':
      return typeof value === 'string' && isValidDate(value) ? null : 'must be a date (YYYY-MM-DD)';
    case 'dropdown':
      return typeof value === 'string' && spec.options.includes(value)
        ? null
        : 'must be one of the listed options';
    case 'boolean':
      return typeof value === 'boolean' ? null : 'must be yes or no';
  }
}

/**
 * Turns what a person typed (or picked) into a stored value. An empty input clears the value.
 * Booleans come from a select ("yes" / "no" / "").
 */
export function parseFieldInput(spec: FieldSpec, raw: string): ParseResult {
  const input = raw.trim();
  if (input === '') return { ok: true, value: null };
  let value: FieldValue;
  switch (spec.type) {
    case 'text':
      value = spec.systemKey === 'email' ? input.toLowerCase() : input;
      if (value.length > TEXT_MAX) return fail(`Use at most ${TEXT_MAX} characters`);
      if (spec.systemKey === 'email' && !EMAIL.safeParse(value).success) {
        return fail('Enter a valid email address');
      }
      break;
    case 'number': {
      const s = input.replace(/,/g, '');
      if (!NUMBER_PATTERN.test(s)) return fail('Enter a number, like 12 or 3.5');
      value = Number(s);
      break;
    }
    case 'currency': {
      const cents = parseCurrencyToCents(input);
      if (cents === null) return fail('Enter an amount, like 25,000 or 1500.50');
      value = cents;
      break;
    }
    case 'date':
      if (!isValidDate(input)) return fail('Enter a valid date');
      value = input;
      break;
    case 'dropdown':
      if (!spec.options.includes(input)) return fail('Choose one of the options');
      value = input;
      break;
    case 'boolean':
      if (input !== 'yes' && input !== 'no') return fail('Choose yes or no');
      value = input === 'yes';
      break;
  }
  const error = checkFieldValue(spec, value);
  return error ? fail(`This value ${error}`) : { ok: true, value };
}

/** The string an edit control starts with (inverse of parseFieldInput). */
export function toInputString(spec: FieldSpec, value: unknown): string {
  if (value === null || value === undefined) return '';
  switch (spec.type) {
    case 'currency':
      return typeof value === 'number' ? formatCents(value).slice(1) : '';
    case 'boolean':
      return value === true ? 'yes' : value === false ? 'no' : '';
    case 'number':
      return typeof value === 'number' ? String(value) : '';
    case 'text':
    case 'date':
    case 'dropdown':
      return typeof value === 'string' ? value : '';
  }
}

/** A stored value that does not fit its field type (e.g. after a type change), shown as-is. */
function raw(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/** Human-readable value for profiles and the timeline. */
export function formatFieldValue(spec: FieldSpec, value: unknown): string {
  if (value === null || value === undefined) return '';
  switch (spec.type) {
    case 'currency':
      return typeof value === 'number' ? formatCents(value) : raw(value);
    case 'number':
      return typeof value === 'number'
        ? value.toLocaleString('en-US', { maximumFractionDigits: 6 })
        : raw(value);
    case 'boolean':
      return value === true ? 'Yes' : value === false ? 'No' : raw(value);
    case 'date': {
      if (typeof value !== 'string' || !isValidDate(value)) return raw(value);
      const [y, m, d] = value.split('-').map(Number);
      return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)).toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    case 'text':
    case 'dropdown':
      return raw(value);
  }
}
